import { execute } from '@/graphql/execute';
import type { ImagesQuery } from '@/graphql/graphql';
import { imageLayerPackQuery } from '@/queries/image.query';

type Image = ImagesQuery['imagesByLayer'][number];

/**
 * Twin of `server/api/src/graphql/resolvers/query/imageLayerPack.ts` (v1): the
 * `imageLayerPack` query's compact form, calibration once per camera. Both
 * tests pin the same sample.
 */
interface PackedCamera {
  width: number;
  height: number;
  pixelWidth: number;
  pixelHeight: number;
  focalLengthX: number | null;
  focalLengthY: number | null;
  principalPointX: number | null;
  principalPointY: number | null;
  cameraType: number | null;
  k1: number | null;
  k2: number | null;
  k3: number | null;
  k4: number | null;
  p1: number | null;
  p2: number | null;
}

interface PackedImage {
  id: string;
  l: number;
  c: number;
  g: number;
  s: number;
  p: number[];
  w: number[];
  r: number[];
  q: number[];
  f: string;
  hc?: true;
  sf?: string;
  lf?: string;
  hb?: true;
  bf?: string;
  sbf?: string;
  lbf?: string;
  ca: string;
  ua: string;
}

export interface ImageLayerPack {
  v: 1;
  layers: { id: string | null; organizationId: string; projectId: string }[];
  cameras: PackedCamera[];
  images: PackedImage[];
}

/** Expands an `imageLayerPack` result into the rows `imagesByLayer` returns. */
export function expandImageLayerPack(pack: ImageLayerPack): Image[] {
  if (pack.v !== 1) throw new Error(`Unsupported imageLayerPack version ${pack.v}`);
  return pack.images.map((image) => {
    const layer = pack.layers[image.l];
    return {
      id: image.id,
      organizationId: layer.organizationId,
      projectId: layer.projectId,
      attachmentId: null,
      layerId: layer.id,
      locationWGS84: { srid: 4326, coords: image.w },
      location: { srid: image.s, coords: image.p },
      groupId: image.g,
      ...pack.cameras[image.c],
      rotationMatrix: image.r,
      rotationQuaternion: image.q,
      filename: image.f,
      hasCompressedVariants: image.hc === true,
      smallFilename: image.sf ?? null,
      largeFilename: image.lf ?? null,
      hasBlurredVariants: image.hb === true,
      blurredFilename: image.bf ?? null,
      smallBlurredFilename: image.sbf ?? null,
      largeBlurredFilename: image.lbf ?? null,
      // DateTime is typed Date but arrives as an ISO string from imagesByLayer too.
      createdAt: image.ca as unknown as Date,
      updatedAt: image.ua as unknown as Date,
    };
  });
}

/** The images of whole layers, fetched compact and expanded. Same rows as `imagesByLayer`. */
export async function fetchImageLayers(layerIds: string[]): Promise<Image[]> {
  if (layerIds.length === 0) return [];
  const data = await execute(imageLayerPackQuery, { layerIds });
  return expandImageLayerPack(data.imageLayerPack as ImageLayerPack);
}
